import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Alert,
} from '@mui/material';
import {
  ShoppingBag,
  LocalShipping,
  CheckCircle,
  Cancel,
  ArrowBack,
  CreditCard,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { orderAPI } from '../services/api';

const OrderDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    fetchOrder();
  }, [isAuthenticated, authLoading, id, navigate]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await orderAPI.getById(id);
      console.log('Order detail response:', response);

      const orderData = response.data || response;
      setOrder(orderData);
    } catch (err) {
      console.error('Error fetching order:', err);
      setError(err.message || 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }

    setCancelling(true);
    try {
      await orderAPI.cancel(id);
      alert('Order cancelled successfully');
      fetchOrder();
    } catch (err) {
      console.error('Error cancelling order:', err);
      alert(err.message || 'Failed to cancel order');
    } finally {
      setCancelling(false);
    }
  };

  const getStatusColor = (status) => {
    const upperStatus = status?.toUpperCase();
    switch (upperStatus) {
      case 'DELIVERED':
        return 'success';
      case 'SHIPPED':
        return 'info';
      case 'PLACED':
      case 'PROCESSING':
        return 'warning';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    const upperStatus = status?.toUpperCase();
    switch (upperStatus) {
      case 'DELIVERED':
        return <CheckCircle />;
      case 'SHIPPED':
        return <LocalShipping />;
      case 'CANCELLED':
        return <Cancel />;
      default:
        return <ShoppingBag />;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          pt: 12,
          pb: 8,
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box sx={{ pt: 12, pb: 8, minHeight: '60vh' }}>
        <Container>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || 'Order not found'}
          </Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/orders')}
            sx={{
              borderRadius: 50,
              background: 'linear-gradient(135deg, #C9A961 0%, #B89850 100%)',
            }}
          >
            Back to Orders
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: { xs: 9, md: 10 }, pb: 8, bgcolor: '#F8F6F1' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/orders')}
            sx={{ mb: 2, color: 'text.secondary' }}
          >
            Back to Orders
          </Button>
          <Typography
            className='font2'
            variant="h3"
            sx={{
              fontFamily: "'Cormorant Garamond', serif",
              fontWeight: 700,
              mb: 1,
            }}
          >
            Order Details
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Order #{order.orderNumber}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Left Column */}
          <Grid item xs={12} md={8}>
            {/* Order Status */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={2}
                >
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Order Status
                    </Typography>
                    <Chip
                      className='font05'
                      icon={getStatusIcon(order.status)}
                      label={order.status}
                      color={getStatusColor(order.status)}
                      size="small"
                      sx={{ fontSize: '0.9rem', px: 1 }}
                    />
                  </Box>
                  {order.status !== 'DELIVERED' && order.status !== 'CANCELLED' && (
                    <Button
                      className='font05'
                      variant="outlined"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={handleCancelOrder}
                      disabled={cancelling}
                      sx={{ borderRadius: 50 }}
                    >
                      {cancelling ? 'Cancelling...' : 'Cancel Order'}
                    </Button>
                  )}
                </Stack>

                {order.trackingNumber && (
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Tracking Number
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {order.trackingNumber}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Order Items */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                  <ShoppingBag sx={{ color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Order Items
                  </Typography>
                </Stack>

                <Stack spacing={2} divider={<Divider />}>
                  {order.items && order.items.map((item, index) => (
                    <Stack key={index} direction="row" spacing={2}>
                      <Box
                        component="img"
                        src={item.productId?.images?.[0] || 'https://via.placeholder.com/80'}
                        alt={item.productId?.name || 'Product'}
                        sx={{
                          width: 80,
                          height: 80,
                          objectFit: 'cover',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {item.productId?.name || 'Product'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.variantId?.weight} - {item.variantId?.packaging}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Quantity: {item.quantity}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {order.currencySymbol || '$'}{(item.displayPrice * item.quantity).toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {order.currencySymbol || '$'}{item.displayPrice?.toFixed(2)} each
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>

            {/* Delivery Address */}
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <HomeIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Delivery Address
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-line',
                    lineHeight: 1.8,
                    color: 'text.secondary',
                  }}
                >
                  {order.shippingAddress}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Payment Summary */}
          <Grid item xs={12} md={4}>
            <Card sx={{ position: 'sticky', top: 100 }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                  <CreditCard sx={{ color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Payment Summary
                  </Typography>
                </Stack>

                <Stack spacing={2} sx={{ mb: 3 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Subtotal
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {order.currencySymbol || '$'}{(order.displayTotalAmount).toFixed(2)}
                    </Typography>
                  </Stack>

                  {order.discountAmount > 0 && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="success.main">
                        Discount
                      </Typography>
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                        -{order.currencySymbol || '$'}{order.discountAmount?.toFixed(2)}
                      </Typography>
                    </Stack>
                  )}

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Delivery
                    </Typography>
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                      FREE
                    </Typography>
                  </Stack>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" justifyContent="space-between" sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Total Paid
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: 'primary.main',
                    }}
                  >
                    {order.currencySymbol || '$'}{order.displayFinalAmount?.toFixed(2)}
                  </Typography>
                </Stack>

                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Payment Method
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {order.paymentMethod}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Payment Status
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: order.paymentStatus === 'PAID' ? 'success.main' : 'warning.main',
                      }}
                    >
                      {order.paymentStatus}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Order Date
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {new Date(order.createdAt).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default OrderDetailPage;
